import { Dates }		from "ts-base/dates";
import { Numbers }		from "ts-base/numbers";
import { Equal }		from "ts-base/equal";
import { Comparator }	from "ts-base/comparator";

import * as constant	from "@v3/data/time/constant";
import { IsoWeek }		from "@v3/data/time/isoWeek";
import { Gregorian }	from "@v3/data/time/gregorian";

export type BusinessWeek = {
	year: number,
	// starting at 1
	week: number,
};

export namespace BusinessWeek {
	export const fromIsoWeek = (d:IsoWeek):BusinessWeek => {
		const parts	= d.split("-W");
		const year	= parseInt(parts[0]);
		const week	= parseInt(parts[1]);
		return { year, week };
	};

	export const toIsoWeek = (it:BusinessWeek):IsoWeek =>
		IsoWeek.brand(`${Numbers.intDigits(it.year, 4)}-W${Numbers.intDigits(it.week, 2)}`);

	//-----------------------------------------------------------------------------

	export const ofGregorian = (gregor:Gregorian):BusinessWeek => {
		const inputDate			= Gregorian.utc.toDate(gregor);

		// calculate thursday in the week of the input
		const thisThursday		= Dates.utc.later(inputDate, { days: 4 - Dates.utc.weekdayNumber(inputDate) });
		const thisThursdayYear	= thisThursday.getUTCFullYear();

		if (thisThursdayYear > gregor.year) {
			// thursday is next year, so we are in the first week of the next year
			return {
				year:	thisThursdayYear,
				week:	1,
			};
		}
		else if (thisThursdayYear < gregor.year) {
			// thursday is in the previous year, so we are in the last week of the previous year
			return {
				year:	thisThursdayYear,
				week:	numberInYear(thisThursdayYear),
			};
		}
		else {
			const jan1			= Gregorian.utc.toDate({ day: 1, month: 1, year: thisThursdayYear });
			const firstThursday	= Dates.utc.later(jan1, { days: (11 - Dates.utc.weekdayNumber(jan1)) % 7 });

			// from thursday to thursday, so we can just count weeks
			const weeksElapsed	= (thisThursday.getTime() - firstThursday.getTime()) / constant.millisPerDay / 7;

			const week	= Math.round(weeksElapsed) + 1;

			return {
				year:	thisThursdayYear,
				week:	week,
			};
		}
	};

	export type GregorianRange	= Readonly<{
		start:	Gregorian,
		end:	Gregorian,
	}>;

	// algorithm taken from https://en.wikipedia.org/wiki/ISO_week_date
	export const asGregorianRange = (it:BusinessWeek):GregorianRange => {
		// TODO kw use this when we actually get a BusinessDay - for now, we assume monday
		const businessDayNumber	= 1;

		const jan1	= Gregorian.utc.toDate({ day: 1, month: 1, year: it.year });
		const jan4	= Gregorian.utc.toDate({ day: 4, month: 1, year: it.year });

		// offsets < 0 and offset > 365 are handler by Dates.utc.later
		const offset	= it.week * 7 + businessDayNumber - (Dates.utc.weekdayNumber(jan4) + 4);
		const start		= Dates.utc.later(jan1,		{ days: offset });
		const end		= Dates.utc.later(start,	{ days: 6 });

		return {
			start:	Gregorian.utc.fromDate(start),
			end:	Gregorian.utc.fromDate(end),
		};
	};

	// TODO time use a Year type for this
	/** how many business weeks a year has */
	export const numberInYear = (year:number):number => {
		const date1 = Gregorian.utc.toDate({ year,	month: 1,	day: 1 });
		const date2 = Gregorian.utc.toDate({ year,	month: 12,	day: 31 });
		const wd1	= Dates.utc.weekdayNumber(date1);
		const wd2	= Dates.utc.weekdayNumber(date2);
		return wd1 === 4 || wd2 === 4 ? 53 : 52;
	};

	//-----------------------------------------------------------------------------

	export const equal:Equal<BusinessWeek>	= (a, b) =>
		a.year === b.year	&&
		a.week === b.week;

	export const compare:Comparator<BusinessWeek> =
		Comparator.concat(
			Comparator.contraMap(Numbers.compare)((it:BusinessWeek) => it.year),
			Comparator.contraMap(Numbers.compare)((it:BusinessWeek) => it.week)
		);
};
